import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CalendarCheck } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ROUTES } from "../../routes";
import { selectMemberUpcomingSeats } from "../../features/sessions/seats-slice";
import { fetchMemberUpcomingSeats } from "../../features/sessions/slice-extensions/occupancy-slice-extension";
import { formatSeatDateTime } from "../../features/sessions/utils/seatDateTime";

interface MemberUpcomingSeatsCardProps {
  memberId: string;
}

/**
 * "Upcoming booked dates" on the member page: the member's single-date seats (portal,
 * slot and on-behalf bookings) from today on, as a plain hairline list with the status
 * in words ("Booked" / "Request open") and a link to each Session. Hidden when there
 * are none. "Book a date" and "Book a slot" refresh it through the seats slice.
 */
const MemberUpcomingSeatsCard = ({ memberId }: MemberUpcomingSeatsCardProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const seats = useAppSelector(selectMemberUpcomingSeats(memberId));

  useEffect(() => {
    dispatch(fetchMemberUpcomingSeats(memberId));
  }, [dispatch, memberId]);

  if (seats.length === 0) return null;

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-center gap-2">
        <CalendarCheck size={20} className="text-primary-40 dark:text-primary-90" />
        <h3 className="text-grey-10 dark:text-grey-90">{t("members.upcomingSeats.title")}</h3>
      </div>
      <ul className="divide-y divide-grey-90 border-t border-grey-90 dark:divide-primary-20 dark:border-primary-20">
        {seats.map((seat) => (
          <li key={`${seat.bookingId}-${seat.date}`} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <Link
                to={ROUTES.SESSION_DETAILS.replace(":id", seat.bookingId)}
                className="block truncate text-sm font-medium text-grey-20 dark:text-white cursor-pointer hover:underline"
              >
                {seat.sessionName}
              </Link>
              <p className="truncate text-xs text-grey-40 dark:text-grey-80">
                {formatSeatDateTime(seat.date, seat.start, i18n.language)}
              </p>
            </div>
            {seat.isPending ? (
              <span className="shrink-0 text-sm text-grey-40 dark:text-grey-70">
                {t("members.upcomingSeats.requested")}
              </span>
            ) : (
              <span className="shrink-0 text-sm text-green-700 dark:text-green-400">
                {t("seats.statusConfirmed")}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MemberUpcomingSeatsCard;
